import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  BadgePercent,
  BookOpen,
  BookText,
  Bot,
  Braces,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  FileBarChart,
  FileText,
  Globe,
  LayoutDashboard,
  Lightbulb,
  LineChart,
  Link2,
  LogOut,
  Megaphone,
  Menu,
  PlusCircle,
  Search,
  Settings as SettingsIcon,
  Shield,
  UserCog,
  ShieldAlert,
  ShieldCheck,
  Swords,
  X,
} from 'lucide-react';
import { api } from '../api/client';
import { RESELLER_CONSOLE_ENABLED } from '../lib/uiFlags';
import { hasFeature } from '../lib/features';
import { useAuthStore } from '../store/auth';
import { useSelectedDomain } from '../store/selectedDomain';
import Logo from '../components/Logo';
import AppFooter from '../components/AppFooter';
import { SkeletonPage } from '../components/Skeleton';
import OverviewTab from './dashboard/OverviewTab';
import AITrafficTab from './dashboard/AITrafficTab';
import BotActivityTab from './dashboard/BotActivityTab';
import ContentTab from './dashboard/ContentTab';
import AuditTab from './dashboard/AuditTab';
import SchemaTab from './dashboard/SchemaTab';
import GapsTab from './dashboard/GapsTab';
import KeywordsTab from './dashboard/KeywordsTab';
import ReportsTab from './dashboard/ReportsTab';
import CompeteTab from './dashboard/CompeteTab';
import SalesTab from './dashboard/SalesTab';
import SalesTemplatesLibraryTab from './dashboard/SalesTemplatesLibraryTab';
import CitationsTab from './dashboard/CitationsTab';
import ClientsTab from './dashboard/ClientsTab';
import SettingsTab from './dashboard/SettingsTab';
import AccountTab from './dashboard/AccountTab';
import LibraryTab from './dashboard/LibraryTab';
import ResellerRightsTab from './dashboard/ResellerRightsTab';
import AiKeyErrorBanner from '../components/AiKeyErrorBanner';
import EmptyState from '../components/EmptyState';

// Set when a brand-new user clicks "Skip for now" on the add-first-domain
// screen, so the dashboard shows an empty state instead of bouncing them back.
const ONBOARDING_SKIPPED_KEY = 'rai_onboarding_skipped';

// Grouped sidebar nav. Each group has a label + items; Settings sits separately
// at the bottom of the sidebar.
const NAV_GROUPS = [
  {
    label: 'Track',
    items: [
      { to: '', label: 'Overview', icon: LayoutDashboard },
      { to: 'traffic', label: 'AI Traffic', icon: LineChart },
      { to: 'bots', label: 'Crawlers', icon: Bot },
    ],
  },
  {
    label: 'Create',
    items: [
      { to: 'content', label: 'Content', icon: FileText },
      { to: 'gaps', label: 'Gaps', icon: Lightbulb },
      { to: 'keywords', label: 'Keywords', icon: Search },
      { to: 'schema', label: 'Schema', icon: Braces },
      { to: 'library', label: 'Library', icon: BookOpen },
    ],
  },
  {
    label: 'Improve',
    items: [
      { to: 'audit', label: 'Audit', icon: ClipboardCheck },
      { to: 'compete', label: 'Compete', icon: Swords },
      { to: 'citations', label: 'Citations', icon: Link2 },
    ],
  },
  {
    label: 'Sell',
    items: [
      { to: 'clients', label: 'Clients', icon: Briefcase },
      { to: 'reports', label: 'Reports', icon: FileBarChart },
      { to: 'sales', label: 'Sales', icon: Megaphone },
      { to: 'sales-templates', label: 'Templates', icon: BookText },
      // Enterprise-only — hidden for everyone else (see Sidebar filter).
      { to: 'reseller-rights', label: 'Reseller Rights', icon: BadgePercent, feature: 'resellerLicensing' },
    ],
  },
];

const SETTINGS_ITEM = { to: 'settings', label: 'Domain settings', icon: SettingsIcon };
const ACCOUNT_ITEM = { to: 'account', label: 'Account', icon: UserCog };

export default function Dashboard() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { domainId, setDomainId } = useSelectedDomain();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get('/api/domains').then((r) => r.data.domains),
  });

  useEffect(() => {
    if (!data) return;
    if (data.length === 0) {
      // First-run nudge to add a domain — unless the user explicitly skipped,
      // in which case we let them sit on an empty-state dashboard.
      if (!localStorage.getItem(ONBOARDING_SKIPPED_KEY)) {
        navigate('/domains/new');
      }
      return;
    }
    if (!domainId || !data.find((d) => d._id === domainId)) {
      setDomainId(data[0]._id);
    }
  }, [data, domainId, navigate, setDomainId]);

  const selected = data?.find((d) => d._id === domainId);

  const noDomainState = (
    <EmptyState
      icon={Globe}
      title="No domain yet"
      body="Add your first site to start tracking AI crawlers, run a GEO audit, and generate your llms.txt."
      action={
        <button className="btn-primary" onClick={() => navigate('/domains/new')}>
          <PlusCircle className="w-4 h-4" /> Add domain
        </button>
      }
    />
  );
  // Domain-specific tabs need a selected domain — show the empty state when
  // there isn't one. Account-level tabs (account, library, templates, reseller
  // rights) render regardless, so a domain-less user can still reach them.
  const withDomain = (node) => (selected ? node : noDomainState);

  return (
    <div className="min-h-screen flex">
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={user}
      />

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          user={user}
          domains={data}
          selected={selected}
          domainId={domainId}
          setDomainId={setDomainId}
          onOpenDrawer={() => setDrawerOpen(true)}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onLogout={() => {
            logout();
            navigate('/login');
          }}
        />

        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <AiKeyErrorBanner />
          {isLoading ? (
            <SkeletonPage stats={4} withTable />
          ) : (
            <Routes>
              <Route index element={withDomain(<OverviewTab domain={selected} />)} />
              <Route path="traffic" element={withDomain(<AITrafficTab domain={selected} />)} />
              <Route path="bots" element={withDomain(<BotActivityTab domain={selected} />)} />
              <Route path="content" element={withDomain(<ContentTab domain={selected} />)} />
              <Route path="gaps" element={withDomain(<GapsTab domain={selected} />)} />
              <Route path="keywords" element={withDomain(<KeywordsTab domain={selected} />)} />
              <Route path="schema" element={withDomain(<SchemaTab domain={selected} />)} />
              <Route path="library" element={<LibraryTab />} />
              <Route path="audit" element={withDomain(<AuditTab domain={selected} />)} />
              <Route path="compete" element={withDomain(<CompeteTab domain={selected} />)} />
              <Route path="citations" element={withDomain(<CitationsTab domain={selected} />)} />
              <Route path="clients/*" element={withDomain(<ClientsTab domain={selected} />)} />
              <Route path="reports" element={withDomain(<ReportsTab domain={selected} />)} />
              <Route path="sales" element={withDomain(<SalesTab domain={selected} />)} />
              <Route path="sales-templates" element={<SalesTemplatesLibraryTab />} />
              <Route path="reseller-rights" element={<ResellerRightsTab />} />
              <Route path="settings/*" element={withDomain(<SettingsTab domain={selected} />)} />
              <Route path="account/*" element={<AccountTab />} />
            </Routes>
          )}
        </main>

        <AppFooter />
      </div>
    </div>
  );
}

function Sidebar({ open, onClose, user }) {
  // Reseller v2 §H — probe /api/me/reseller to decide whether to surface the
  // Reseller console link. Cached + retry-less so signed-in non-resellers
  // don't pay repeated 403 round-trips.
  const resellerProbe = useQuery({
    queryKey: ['me-reseller-probe'],
    queryFn: () => api.get('/api/me/reseller').then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled: RESELLER_CONSOLE_ENABLED, // skip the probe entirely while hidden
  });
  const hasReseller = !!resellerProbe.data?.reseller;
  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          onClick={onClose}
          className="lg:hidden fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-30"
        />
      )}

      <aside
        className={clsx(
          'fixed lg:sticky top-0 left-0 h-screen w-64 bg-white border-r border-gray-100 z-40 flex flex-col',
          'transform transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b border-gray-100 flex-none">
          <Logo size="md" />
          <button
            type="button"
            className="lg:hidden btn-icon"
            onClick={onClose}
            title="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 scroll-soft">
          {NAV_GROUPS.map((g) => (
            <div key={g.label} className="mb-5 last:mb-0">
              <div className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                {g.label}
              </div>
              <ul className="space-y-0.5">
                {g.items
                  .filter((it) => !it.feature || hasFeature(user, it.feature))
                  .map((it) => (
                    <SidebarLink key={it.label} item={it} onClick={onClose} />
                  ))}
              </ul>
            </div>
          ))}

          {(user?.role === 'admin' || (RESELLER_CONSOLE_ENABLED && hasReseller)) && (
            <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
              {user?.role === 'admin' && (
                <NavLink
                  to="/admin"
                  onClick={onClose}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-brand-600 to-cyan-500 text-white hover:brightness-110 transition"
                >
                  <Shield className="w-4 h-4" />
                  Admin console
                </NavLink>
              )}
              {RESELLER_CONSOLE_ENABLED && hasReseller && (
                <NavLink
                  to="/reseller"
                  onClick={onClose}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-violet-600 to-cyan-500 text-white hover:brightness-110 transition"
                >
                  <Briefcase className="w-4 h-4" />
                  Reseller console
                </NavLink>
              )}
            </div>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-gray-100 flex-none space-y-1">
          <SidebarLink item={SETTINGS_ITEM} onClick={onClose} />
          <SidebarLink item={ACCOUNT_ITEM} onClick={onClose} />
        </div>
      </aside>
    </>
  );
}

function SidebarLink({ item, onClick }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === ''}
        onClick={onClick}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            isActive
              ? 'bg-brand-50 text-brand-700'
              : 'text-ink-700 hover:bg-gray-50 hover:text-ink-900',
          )
        }
      >
        <Icon className="w-4 h-4 flex-none" />
        <span className="truncate">{item.label}</span>
      </NavLink>
    </li>
  );
}

function TopBar({
  user,
  domains,
  selected,
  domainId,
  setDomainId,
  onOpenDrawer,
  menuOpen,
  setMenuOpen,
  onLogout,
}) {
  const navigate = useNavigate();
  return (
    <header className="bg-white/80 backdrop-blur border-b border-gray-100 sticky top-0 z-20">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            className="lg:hidden btn-icon"
            onClick={onOpenDrawer}
            title="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          {domains && domains.length > 0 && (
            <>
              <div className="relative">
                <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                <select
                  className="appearance-none bg-gray-50 hover:bg-gray-100 border border-gray-200
                             rounded-lg pl-8 pr-8 py-1.5 text-sm font-medium
                             focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none
                             cursor-pointer max-w-[12rem] sm:max-w-xs truncate"
                  value={domainId || ''}
                  onChange={(e) => {
                    if (e.target.value === '__new__') navigate('/domains/new');
                    else setDomainId(e.target.value);
                  }}
                >
                  {domains.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.displayName || d.domain}
                    </option>
                  ))}
                  <option value="__new__">+ Add new domain</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              </div>
              {selected?.verified ? (
                <span className="hidden md:inline-flex pill-success">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="hidden md:inline-flex pill-warn">
                  <ShieldAlert className="w-3 h-3" /> Not verified
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-gray-100 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-brand-500 to-cyan-500 text-white flex items-center justify-center text-sm font-semibold ring-2 ring-white shadow-sm">
                {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
              </div>
              <span className="hidden sm:inline text-sm font-medium">
                {user?.name || user?.email}
              </span>
              <ChevronDown className="hidden sm:inline w-3.5 h-3.5 text-ink-400" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-60 rounded-xl bg-white shadow-cardHover border border-gray-100 py-2 z-20">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-sm font-medium truncate">
                      {user?.name || 'Account'}
                    </div>
                    <div className="text-xs text-ink-400 truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/domains/new');
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2.5"
                  >
                    <PlusCircle className="w-4 h-4 text-ink-400" />
                    Add domain
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/admin');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2.5"
                    >
                      <Shield className="w-4 h-4 text-ink-400" />
                      Admin console
                    </button>
                  )}
                  <div className="my-1 h-px bg-gray-100" />
                  <button
                    onClick={onLogout}
                    className="w-full text-left px-3 py-2 text-sm text-accent-red hover:bg-red-50 flex items-center gap-2.5"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
